import React from 'react';
import { useSelector } from 'react-redux';
import { TypeValue } from 'ha/types/SearchFilters';
import Helmet from 'react-helmet';

import { Typography, Container, Grid, Link } from '@hbf/dsl';
import { useMediaQuery } from 'ha/helpers/MediaQuery';
import { useIntl } from 'ha/i18n';
import { useStyles } from './SearchSEO/SearchCityAbout';

import { getCountryLocalized, getCityLocalized } from '../selectors';

export const SearchBerlinSEO: React.FC = () => {
  const { md } = useMediaQuery();
  const { classes } = useStyles();
  const city = useSelector(getCityLocalized);
  const country = useSelector(getCountryLocalized);
  const { urlResolver } = useIntl();

  const FAQs = [
    {
      '@type': 'Question',
      name: 'What is the Average Rent in Berlin, Germany?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: `The average rent price in Berlin is 1150 EUR (1245 USD) a month. You will find rental prices to range between 9000 to 20000 EUR (9750 to 21650 USD) per year.`,
      },
    },

    {
      '@type': 'Question',
      name: 'Why is rent so expensive in Berlin, Germany?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: `Rent in Berlin is expensive because of the housing shortage. More people are looking for housing in Berlin than the number of houses available for rent.`,
      },
    },

    {
      '@type': 'Question',
      name: 'How to find an accommodation in Berlin, Germany?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: `If you are looking for accommodation in Berlin, then look online for accommodation rental sites like HousingAnywhere. It’s an easy and secure way of finding housing in Berlin.`,
      },
    },
  ];

  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: 'What is the Average Rent in Berlin, Germany?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: `<p> The average rent price in Berlin is 1150 EUR (1245 USD) a month. You will find rental prices to range between 9000 to 20000 EUR (9750 to 21650 USD) per year. </p>`,
        },
      },

      {
        '@type': 'Question',
        name: 'Why is rent so expensive in Berlin, Germany?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: `<p> Rent in Berlin is expensive because of the housing shortage. More people are looking for housing in Berlin than the number of houses available for rent. </p>`,
        },
      },

      {
        '@type': 'Question',
        name: 'How to find an accommodation in Berlin, Germany?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: `<p>If you are looking for accommodation in Berlin, then look online for accommodation rental sites like HousingAnywhere. It’s an easy and secure way of finding housing in Berlin.</p>`,
        },
      },
    ],
  };

  const links = [
    [
      {
        label: 'Berlin apartments for rent',
        category: TypeValue.APARTMENT,
      },
      {
        label: 'Berlin studio apartments',
        category: TypeValue.STUDIO,
      },
      {
        label: 'Student accommodation berlin',
        category: 'student-accommodation',
      },
      {
        label: 'Cheap accommodation in berlin',
        category: 'cheap-accommodation',
      },
    ],

    [
      {
        label: '1 bedroom apartment in berlin',
        category: 'one-bedroom-apartments',
      },
      {
        label: '2 bedroom apartment in berlin',
        category: 'two-bedroom-apartments',
      },
      {
        label: 'Furnished apartments in berlin',
        category: 'furnished-apartments',
      },
      {
        label: 'Long term rentals in berlin',
        category: 'long-term-rentals',
      },
    ],

    [
      {
        label: 'Private room in berlin',
        category: TypeValue.PRIVATE_ROOM,
      },
      {
        label: 'Shared room in berlin',
        category: TypeValue.SHARED_ROOM,
      },
      {
        label: 'Student room in berlin',
        category: 'student-accommodation',
      },
    ],
  ];

  return (
    <React.Fragment>
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>
      <div className={classes.container}>
        <Container>
          <Grid container wrap="nowrap">
            {md && <div className={classes.sidePadding} />}

            <Grid container item xs>
              <Typography.H2 className={classes.title}>
                Accommodation in Berlin, Germany
              </Typography.H2>
              <Typography.Paragraph className={classes.firstParagraph}>
                If you're looking to rent accommodation in Berlin, then you've
                come to the right place. Due to rising demand, houses for rent
                in Berlin remain the most searched ones on HousingAnywhere. You
                can easily find new rental homes in Berlin near you and find
                more information about their size, price, availability, services
                and amenities.
              </Typography.Paragraph>
              <Typography.Paragraph className={classes.secondParagraph}>
                Search through our website and use our search filters to find
                all homes for rent in Berlin. If you are on the lookout for
                housing in Berlin Germany, then you will find a range of
                affordable options to choose from. Due to the standards of
                living and increased international mobility, the rental
                properties in Berlin are always increasing in demand.
              </Typography.Paragraph>
              <Typography.Paragraph className={classes.secondParagraph}>
                If you’re wondering how you can find properties to rent in
                Berlin then look no further, as HousingAnywhere is your go-to
                source for finding accommodations in Berlin Germany from both
                professional agencies and private owners.
              </Typography.Paragraph>
            </Grid>
          </Grid>
        </Container>
      </div>

      <div className={classes.linksContainer}>
        <Container>
          <Grid container wrap="nowrap">
            {md && <div className={classes.sidePadding} />}
            <Grid container item xs flexDirection="column">
              <Typography.H3 className={classes.linkTitle} fontWeight="bold">
                Useful Links
              </Typography.H3>
              <Grid container gap={1} alignItems="flex-start">
                {links.map((linkSection, index) => {
                  return (
                    <div className={classes.berlinLinksConatiner} key={index}>
                      <Grid
                        container
                        item
                        flexDirection="column"
                        className={classes.links}
                        gap={2}
                      >
                        {linkSection.map(link => {
                          return (
                            <Link
                              key={link.label}
                              className={classes.link}
                              to={urlResolver.getSearchUrl(
                                city,
                                country,
                                undefined,
                                link.category,
                              )}
                            >
                              {link.label}
                            </Link>
                          );
                        })}
                      </Grid>
                    </div>
                  );
                })}
              </Grid>
            </Grid>
          </Grid>
        </Container>
      </div>

      <div className={classes.faqSection}>
        <Container>
          <Grid container wrap="nowrap">
            {md && <div className={classes.sidePadding} />}
            <Grid container item xs>
              <Typography.H3 className={classes.faqHeader} fontWeight="bold">
                FAQ
              </Typography.H3>
              <div className={classes.faqContainer}>
                {FAQs.map((faq, index) => {
                  return (
                    <div className={classes.faq} key={index}>
                      <Typography.ParagraphTitle className={classes.faqTitle}>
                        {faq.name}
                      </Typography.ParagraphTitle>
                      <Typography.Paragraph>
                        {faq.acceptedAnswer.text}
                        {/* {T("seo.faq.average_rent.answer")} */}
                      </Typography.Paragraph>
                    </div>
                  );
                })}
              </div>
            </Grid>
          </Grid>
        </Container>
      </div>
    </React.Fragment>
  );
};
